import { ProvisionalAsset } from '../../../store/ProvisionalAsset';
import { DetailPanelParams, FieldLabelMap } from '../../Compounds/DetailPanel/DetailPanel';
import { EditFieldType } from '../../Compounds/DetailPanel/LabelValueField';
import { EntityType } from '../../../store/Search';
import { assetCommissionsStatusOptions } from '../../../store/GeneralStore';
import { CommissionsValidationsSubStatusOptions, LegacyArchiveReason } from '../../../utils/constants';

export const FIELD_LABEL_MAP: FieldLabelMap = {
  id: 'ID',
  accountId: 'Customer',
  supplier: 'Supplier',
  salesAgent: 'Sales Agent',
  tsd: 'TSD',
  product2id: 'Product',
  installDate: 'Install Date',
  circuitId: 'Circuit ID',
  cancelledDate: 'Cancelled Date',
  tsdTrackingNumber: 'TSD Tracking Number',
  disconnectDate: 'Disconnect Date',
  commissionStatus: 'Commissions Status',
  actualFirstCommissionRun: 'Actual First Commission Run',
  actualFirstSpiffRun: 'Actual First SPIFF Run',
  expectedDecommissionDate: 'Expected Decommission Run',
  actualDecommissionedDate: 'Actual Decommission Run',
  mrc: 'Total MRC',
  expectedGrossMrr: 'Gross MRR',
  commissionableMrc: 'Commissionable MRC',
  expectedNetMrr: 'Net MRR',
  recurringCommission: 'Recurring Commission %',
  commissionableNrc: 'Commissionable NRC',
  expectedGrossNrr: 'Gross NRR',
  nonCommissionableNrc: 'Non Commissionable NRC',
  expectedNetNrr: 'Net NRR',
  nonRecurringCommission: 'Non Recurring Commission %',
  commissionsExperienceAnalyst: 'Commissions Experience Analyst',
  location: 'Provisional Location',
  commissionsValidatedDate: 'Commissions Validated Date',
  commissionsValidationStatus: 'Commissions Validation Status',
  commissionsValidationSubStatus: 'Commissions Validation Sub Status',
  active: 'Active',
  createdAt: 'Create Date',
  fromId: 'Provisional From ID',
  toId: 'Provisional To ID',
  provisionalType: 'Provisional Type',
  origin: 'Provisional Origin',
  paymentFrequency: 'Payment Frequency',
  legacyArchiveReason: 'Legacy Archive Reason'
};

export const provisionalAssetSectionsInfo = (data: ProvisionalAsset): DetailPanelParams[] => [
  {
    data,
    canEdit: [
      {
        field: 'accountId',
        type: EditFieldType.SEARCHBOX,
        mapType: EntityType.CUSTOMER
      },
      {
        field: 'salesAgent',
        type: EditFieldType.SEARCHBOX,
        mapType: EntityType.AP
      },
      {
        field: 'supplier',
        type: EditFieldType.SEARCHBOX,
        mapType: EntityType.SUPPLIER
      },
      {
        field: 'tsd',
        type: EditFieldType.SEARCHBOX,
        mapType: EntityType.TSD
      }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['accountId', 'salesAgent', 'supplier', 'tsd'],
    associationNameFields: {
      accountId: 'accountName',
      salesAgent: 'salesAgentName',
      supplier: 'supplierName',
      tsd: 'tsdName'
    }
  },
  {
    data,
    canEdit: [],
    fieldMap: FIELD_LABEL_MAP,
    order: ['commissionsExperienceAnalyst', 'location']
  },
  {
    data,
    canEdit: [
      { field: 'tsdTrackingNumber', type: EditFieldType.TEXT },
      { field: 'circuitId', type: EditFieldType.TEXT },
      { field: 'installDate', type: EditFieldType.DATE },
      { field: 'disconnectDate', type: EditFieldType.DATE },
      { field: 'cancelledDate', type: EditFieldType.DATE },
      {
        field: 'product2id',
        type: EditFieldType.SEARCHBOX,
        mapType: EntityType.PRODUCT
      }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['product2id', 'circuitId', 'installDate', 'tsdTrackingNumber', 'cancelledDate', '', 'disconnectDate'],
    associationNameFields: {
      product2id: 'productName'
    }
  }
];

export const commissionsSectionInfo = (data: ProvisionalAsset): DetailPanelParams[] => [
  {
    data,
    canEdit: [
      {
        field: 'commissionStatus',
        type: EditFieldType.SELECT,
        options: [null, ...assetCommissionsStatusOptions]
      },
      {
        field: 'actualFirstCommissionRun',
        type: EditFieldType.DATE
      },
      {
        field: 'actualDecommissionedDate',
        type: EditFieldType.DATE
      },
      {
        field: 'actualFirstSpiffRun',
        type: EditFieldType.DATE
      }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: [
      'commissionStatus',
      'actualFirstCommissionRun',
      'expectedDecommissionDate',
      'actualDecommissionedDate',
      '',
      'actualFirstSpiffRun'
    ]
  },
  {
    data,
    canEdit: [
      { field: 'commissionableMrc', type: EditFieldType.CURRENCY },
      { field: 'mrc', type: EditFieldType.CURRENCY },
      { field: 'expectedGrossMrr', type: EditFieldType.CURRENCY },
      { field: 'recurringCommission', type: EditFieldType.PERCENT }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['mrc', 'commissionableMrc', 'recurringCommission', 'expectedGrossMrr']
  },
  {
    data,
    canEdit: [
      { field: 'nonCommissionableNrc', type: EditFieldType.CURRENCY },
      { field: 'commissionableNrc', type: EditFieldType.CURRENCY },
      { field: 'expectedGrossNrr', type: EditFieldType.CURRENCY },
      { field: 'nonRecurringCommission', type: EditFieldType.PERCENT }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['nonCommissionableNrc', 'commissionableNrc', 'nonRecurringCommission', 'expectedGrossNrr']
  },
  {
    data,
    canEdit: [
      { field: 'expectedNetMrr', type: EditFieldType.CURRENCY },
      { field: 'expectedNetNrr', type: EditFieldType.CURRENCY }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['expectedNetMrr', 'expectedNetNrr']
  }
];

export const assetValidationInfo = (data: ProvisionalAsset): DetailPanelParams[] => [
  {
    data,
    canEdit: [
      {
        field: 'commissionsValidatedDate',
        type: EditFieldType.DATE
      },
      {
        field: 'commissionsValidationStatus',
        type: EditFieldType.SELECT,
        options: [null, 'Validated', 'Could Not Validate', 'Partially Validated']
      },
      {
        field: 'commissionsValidationSubStatus',
        type: EditFieldType.MULTI_SELECT,
        options: CommissionsValidationsSubStatusOptions
      },
      {
        field: 'paymentFrequency',
        type: EditFieldType.SELECT,
        options: [null]
      },
      {
        field: 'legacyArchiveReason',
        type: EditFieldType.SELECT,
        options: [null, ...Object.values(LegacyArchiveReason)]
      }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: [
      'commissionsValidationStatus',
      'commissionsValidationSubStatus',
      'commissionsValidatedDate',
      'paymentFrequency',
      'legacyArchiveReason'
    ]
  }
];

export const assetSectionMeta = (data: ProvisionalAsset): DetailPanelParams[] => [
  {
    data,
    canEdit: [
      {
        field: 'toId',
        type: EditFieldType.TEXT
      },
      {
        field: 'provisionalType',
        type: EditFieldType.SELECT,
        options: [null, 'Finance Record', 'Unvalidated']
      },
      { field: 'active', type: EditFieldType.CHECKBOX }
    ],
    fieldMap: FIELD_LABEL_MAP,
    order: ['id', 'active', 'fromId', 'provisionalType', 'toId', 'origin', 'createdAt']
  }
];

export const provisionalAssetRpmAccountsInfo = (data: ProvisionalAsset): DetailPanelParams[] => [
  {
    data,
    canEdit: [],
    fieldMap: FIELD_LABEL_MAP,
    order: []
  }
];

export const provisionalCurrencyFields: (keyof ProvisionalAsset)[] = [
  'commissionableMrc',
  'expectedGrossMrr',
  'mrc',
  'commissionableNrc',
  'expectedGrossNrr',
  'nonCommissionableNrc',
  'expectedNetMrr',
  'expectedNetNrr'
];

export const provisionalPercentFields: (keyof ProvisionalAsset)[] = ['nonRecurringCommission', 'recurringCommission'];
